'use client';

import Image from 'next/image';
import { useState } from 'react';
import { addLine } from '@/lib/cart-store';
import { openCartDrawer } from '@/lib/cart-drawer';
import { trackAddToCart } from '@/lib/analytics';

export interface DecantPick {
  productId:  string;
  /** The concrete variant the set adds (a decant's 10mL). Used as the picker key. */
  variantId:  string;
  slug:       string;
  name:       string;
  brand?:     string;
  priceText:  string;
  priceCents: number;
  image?:     string;
  inStock:    boolean;
  maxQty:     number;
  inStockQty: number;
  preorder:   boolean;
  shipsBy:    string | null;
}

const SET_SIZE = 3;

function formatTotal(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function SetBuilder({ decants }: { decants: DecantPick[] }) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected   = selectedIds
    .map((id) => decants.find((d) => d.variantId === id))
    .filter((d): d is DecantPick => !!d);
  const total      = selected.reduce((sum, d) => sum + d.priceCents, 0);
  const remaining  = SET_SIZE - selected.length;

  function toggle(id: string) {
    setError(null);
    setSelectedIds((current) => {
      if (current.includes(id)) return current.filter((x) => x !== id);
      if (current.length >= SET_SIZE) return current;
      return [...current, id];
    });
  }

  async function buildSet() {
    if (selected.length !== SET_SIZE || pending) return;
    setError(null);
    setPending(true);
    try {
      let failed = 0;
      for (const d of selected) {
        const res = addLine(
          {
            variantId:    d.variantId,
            productId:    d.productId,
            slug:         d.slug,
            name:         d.name,
            variantTitle: null,
            image:        d.image,
            priceCents:   d.priceCents,
            maxQty:       d.maxQty,
            inStockQty:   d.inStockQty,
            preorder:     d.preorder,
            shipsBy:      d.shipsBy,
            brand:        d.brand,
          },
          1,
        );
        if (!res.ok) { failed += 1; continue; }
        trackAddToCart({
          item_id:        d.productId,
          item_name:      d.name,
          item_brand:     d.brand,
          item_category:  'Discovery Set',
          price:          d.priceCents / 100,
          quantity:       1,
        });
      }
      if (failed > 0) setError('Some of those are already at the most you can add. Check your bag.');
      // Reset the picker and surface the cart so the customer can see what landed.
      setSelectedIds([]);
      openCartDrawer();
    } catch (err) {
      console.error('[build-a-set] add failed', err);
      setError('Could not add the set — please try again.');
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      {/* Picker grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10">
        {decants.map((d) => {
          const isSelected = selectedIds.includes(d.variantId);
          const limitHit   = !isSelected && selectedIds.length >= SET_SIZE;
          return (
            <button
              key={d.variantId}
              type="button"
              onClick={() => toggle(d.variantId)}
              disabled={limitHit}
              aria-pressed={isSelected}
              className={[
                'group text-left relative transition-opacity disabled:opacity-40 disabled:cursor-not-allowed',
                'focus:outline-none focus:ring-1 focus:ring-ink/40 focus:ring-offset-4 focus:ring-offset-bone',
              ].join(' ')}
            >
              <div
                className={[
                  'relative aspect-[3/4] mb-4 overflow-hidden bg-white',
                  'ring-1 ring-inset transition-all duration-200',
                  isSelected ? 'ring-ink ring-2' : 'ring-ink/10',
                ].join(' ')}
              >
                {d.image && (
                  <Image
                    src={d.image}
                    alt={d.name}
                    fill
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    className="object-contain p-[6%] transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                )}
                {/* Selection chip — top-right, inside the image plate. */}
                <div
                  className={[
                    'absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center',
                    'transition-all duration-200',
                    isSelected ? 'bg-ink text-bone' : 'bg-bone/85 text-ink/40 ring-1 ring-ink/15 group-hover:text-ink/80',
                  ].join(' ')}
                  aria-hidden="true"
                >
                  {isSelected ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="5 13 10 18 19 6" />
                    </svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  )}
                </div>
              </div>
              {d.brand && (
                <p className="text-xs uppercase tracking-[0.12em] text-ink/65 mb-1">{d.brand}</p>
              )}
              <h3 className="font-display tracking-[0.04em] font-semibold text-xs md:text-sm text-ink leading-tight mb-1.5">
                {d.name}
              </h3>
              <p className="text-xs text-ink/65 font-numerals">{d.priceText}{d.preorder && d.inStockQty < 1 ? ' · Pre-order' : ''}</p>
            </button>
          );
        })}
      </div>

      {/* Sticky tray — mirrors the choose-three rhythm from a tasting flight, not a combo meal. */}
      <div className="sticky bottom-0 left-0 right-0 -mx-6 lg:-mx-12 mt-16 bg-bone/95 backdrop-blur border-t border-ink/15 px-6 lg:px-12 py-5 z-30">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="flex items-center gap-5">
            {/* Slots — three small placeholders that fill in as the customer picks. */}
            <div className="flex gap-2" aria-label={`Set progress: ${selected.length} of ${SET_SIZE} selected`}>
              {Array.from({ length: SET_SIZE }).map((_, i) => {
                const d = selected[i];
                return (
                  <div
                    key={i}
                    className={[
                      'w-11 h-11 overflow-hidden bg-white',
                      'ring-1 ring-inset transition-all duration-200',
                      d ? 'ring-ink' : 'ring-ink/15',
                    ].join(' ')}
                  >
                    {d?.image && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={d.image} alt="" width={44} height={44} className="w-full h-full object-contain p-[6%]" />
                    )}
                  </div>
                );
              })}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-ink/65 leading-none">
                {remaining > 0
                  ? `Pick ${remaining} more`
                  : 'Set complete'}
              </p>
              <p className="font-numerals text-base text-ink leading-tight mt-1">
                {selected.length === 0 ? '—' : formatTotal(total)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={buildSet}
            disabled={selected.length !== SET_SIZE || pending}
            data-magnetic={selected.length === SET_SIZE ? '' : undefined}
            className="bg-plum text-white px-8 py-3.5 text-xs uppercase tracking-[0.12em] font-medium hover:bg-plum-deep transition-colors rounded-sm disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap self-start sm:self-auto"
          >
            {pending
              ? 'Adding…'
              : selected.length === SET_SIZE
              ? `Build set — ${formatTotal(total)}`
              : `Build set — pick ${remaining} more`}
          </button>
        </div>
        {error && (
          <p role="alert" className="text-xs text-ink max-w-[1200px] mx-auto mt-3">
            {error}
          </p>
        )}
      </div>
    </>
  );
}
